import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { MessageSquare, BookOpen, Zap, Loader2, ExternalLink } from 'lucide-react'
import {
  fetchConversations, fetchKbArticles, fetchKbFiles, fetchPipelines,
  type ConversationListItem, type KbArticle, type Pipeline,
} from '@/lib/supportAdminApi'

export default function AdminDashboard() {
  const { user, loading } = useAuth()
  const navigate = useNavigate()

  const [conversations, setConversations] = useState<ConversationListItem[]>([])
  const [articles, setArticles] = useState<KbArticle[]>([])
  const [fileCount, setFileCount] = useState(0)
  const [pipelines, setPipelines] = useState<Pipeline[]>([])
  const [fetching, setFetching] = useState(true)

  useEffect(() => {
    if (loading) return
    if (!user) { navigate('/auth', { replace: true }); return }
  }, [user, loading, navigate])

  useEffect(() => {
    if (!user) return
    Promise.all([
      fetchConversations({ status: 'open', page: 1 }),
      fetchKbArticles(),
      fetchKbFiles(),
      fetchPipelines(),
    ]).then(([convRes, arts, files, pipes]) => {
      setConversations(convRes.items.slice(0, 5))
      setArticles(arts.slice(0, 4))
      setFileCount(files.length)
      setPipelines(pipes)
    }).catch(() => {}).finally(() => setFetching(false))
  }, [user])

  if (loading || fetching) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Visão geral da central de suporte</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Chats Abertos</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{conversations.length}</p>
            <p className="text-xs text-muted-foreground">conversas em andamento</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Base de Conhecimento</CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{articles.length}</p>
            <p className="text-xs text-muted-foreground">{fileCount} arquivo(s) carregado(s)</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Pipelines</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{pipelines.filter(p => p.is_enabled).length}/{pipelines.length}</p>
            <p className="text-xs text-muted-foreground">ativas</p>
          </CardContent>
        </Card>
      </div>

      {/* Detail sections */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Active chats */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3 space-y-0">
            <CardTitle className="text-sm font-medium">Chats em andamento</CardTitle>
            <Button variant="ghost" size="sm" asChild className="h-7 px-2 text-xs">
              <Link to="/admin/conversas">
                Ver todos <ExternalLink className="ml-1 h-3 w-3" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {conversations.length === 0 ? (
              <p className="text-xs text-muted-foreground">Nenhuma conversa aberta.</p>
            ) : (
              <div className="space-y-1">
                {conversations.map(c => (
                  <div key={c.id} className="flex items-center justify-between py-1.5 border-b last:border-0">
                    <span className="text-xs text-muted-foreground truncate max-w-[150px]">
                      {c.first_message ?? 'Sem mensagem'}
                    </span>
                    <Badge variant="secondary" className="text-[10px] ml-2 shrink-0">
                      {c.user_email ?? 'anon'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* KB articles */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3 space-y-0">
            <CardTitle className="text-sm font-medium">Base de Conhecimento</CardTitle>
            <Button variant="ghost" size="sm" asChild className="h-7 px-2 text-xs">
              <Link to="/admin/conhecimento">
                Gerenciar <ExternalLink className="ml-1 h-3 w-3" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {articles.length === 0 ? (
              <p className="text-xs text-muted-foreground">Nenhum artigo cadastrado.</p>
            ) : (
              <div className="space-y-1">
                {articles.map(a => (
                  <div key={a.id} className="py-1.5 border-b last:border-0">
                    <p className="text-xs font-medium truncate">{a.title}</p>
                    <p className="text-[11px] text-muted-foreground truncate">
                      {a.content.slice(0, 70)}{a.content.length > 70 ? '…' : ''}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pipelines */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3 space-y-0">
            <CardTitle className="text-sm font-medium">Pipelines</CardTitle>
            <Button variant="ghost" size="sm" asChild className="h-7 px-2 text-xs">
              <Link to="/admin/pipelines">
                Configurar <ExternalLink className="ml-1 h-3 w-3" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {pipelines.length === 0 ? (
              <p className="text-xs text-muted-foreground">Nenhuma pipeline cadastrada.</p>
            ) : (
              <div className="space-y-1">
                {pipelines.map(p => (
                  <div key={p.id} className="flex items-center justify-between py-1.5 border-b last:border-0">
                    <div className="min-w-0">
                      <p className="text-xs font-medium truncate">{p.display_name}</p>
                      {p.description && (
                        <p className="text-[10px] text-muted-foreground truncate">{p.description}</p>
                      )}
                    </div>
                    <Badge
                      variant={p.is_enabled ? 'default' : 'secondary'}
                      className="text-[10px] ml-2 shrink-0"
                    >
                      {p.is_enabled ? 'Ativa' : 'Inativa'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
